import classNames from 'classnames';
import { Body, BodyProps } from '@components/text';
import './AssistiveText.scss';

export interface AssistiveTextProps extends Omit<BodyProps, 'color' | 'children'> {
  children?: string;
  id?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
}

/**
 * Assistive Text component - Used alongside Input components to provide additional information, e.g. error messages
 * Includes aria attributes for screen readers
 * @param children
 * @param id
 * @param disabled
 * @param error
 * @param className
 * @constructor
 */
export const AssistiveText: React.FC<AssistiveTextProps> = ({
  children,
  id,
  disabled,
  error,
  size = 'small',
  variant = 'caption',
  weight = 'regular',
  className,
  ...rest
}) => {
  // role="alert" implicitly sets aria-live="assertive"; mixing it with an explicit
  // aria-live="polite" on the same element produces conflicting behaviour across
  // screen readers. Use one or the other depending on the error state.
  const ariaProps = error ? { role: 'alert' as const } : { 'aria-live': 'polite' as const };
  return (
    <Body id={id} size={size} weight={weight} variant={variant} {...ariaProps} {...rest}>
      <span
        className={classNames(
          'vds-assistive-text',
          {
            'vds-assistive-text--error': error,
            'vds-assistive-text--disabled': disabled,
          },
          className,
        )}>
        {children}
      </span>
    </Body>
  );
};
